import { ShippingDocumentService } from 'src/app/core/services/shipping-document.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { environment } from 'src/environments/environment';
import { CopyBillBookingRequest } from 'src/app/core/models/transport-operation/copy-bill-booking-request';
import { CopyBillBookingResponse } from 'src/app/core/models/transport-operation/copy-bill-booking-response';
import { ForwarderFetchFedExPodByFacilityAndBookingNoRequest } from 'src/app/features/transport-operation/pages/booking-search/interface/forwarderFetchFedExPodByFacilityAndBookingNoRequest';

export interface ExportDeclarationFirstConditionDto {
  bookingNo: string;
  shippingMethod: string;
  isNeedSecondCondition: string;
  isNeedAutoCreateExportDeclaration: string
}
export interface InvoiceReportDto {
  base64File: string;
}

@Injectable({
  providedIn: 'root'
})
export class BookingService {

  constructor(
    private http: HttpClient,
    private documentService: ShippingDocumentService) { }

  /**
   * Get all DoNo.
   */
  getDoList(bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/${bookingNo}/doNoList`;
    return this.http.get(url);
  }

  /**
   * Get all booking.
   */
  getBookingHeader(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/searchBooking`;
    return this.http.post(url, param);
  }

  /**
   * Get all booking.
   */
  getBookingDetail(facility: string, bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/searchBookingDetail`;
    const headers = new HttpHeaders();
    const params = new HttpParams().append('facility', facility).append('bookingNo', bookingNo);

    return this.http.get(url, { headers, params });
  }

  /**
   * Save DO in to booking.
   */
  saveDoNoIntoBooking(facility: string, importedDOs: any[], userId: string, bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/saveDoNoIntoBooking`;
    const headers = new HttpHeaders();

    return this.http.post(url, { deliveryOrderList: importedDOs, facility, userId, bookingNo });
  }

  /**
   * Cancel booking.
   */
  cancelBooking(bookingNo: string, userId: string, facility: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/updateBookingStatus`;
    const headers = new HttpHeaders();

    return this.http.put(url, { bookingNo, userId, facility });
  }

  /**
   * Update booking
   * @param booking Updated booking data
   */
  updateBooking(booking: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking`;
    console.log('bookingbookingbooking',booking)
    return this.http.put(url, booking);
  }

  /**
   * Update booking details.
   * @param details Booking details
   */
  updateBookingDetails(details: any[]): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/updateBookingDetailsUrgentFlag`;
    return this.http.put(url, details);
  }

  /**
   * Get booking header detail.
   * @param bookingNo Booking No.
   */
  getBookingHeaderDetail(bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/header/${isNullOrEmpty(bookingNo) ? '' : bookingNo}`;
    return this.http.get(url);
  }

  /**
   * Send delivery orders to get preview bookings.
   * @param DOs Delivery order numbers
   */
  getPreviewBookings(DONos: string[], bookingType: string, facility: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/previewBooking`;
    return this.http.post(url, { doNoList: DONos, bookingType: bookingType, facility: facility });
  }

  /**
   * Send delivery orders to get preview bookings. (For special case)
   * @param DONos Delivery order numbers
   * @param targetDONo Target delivery order number
   */
  getPreviewBookingsSpecial(DONos: string[], targetDONo: string, facility: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/previewBookingSpecial`;
    return this.http.post(url, { doNoList: DONos, targetDoNo: targetDONo, facility: facility });
  }

  /**
   * Create booking by giving confirmed preview bookings.
   * @param bookings Confirmed preview bookings
   */
  createMannualBookings(bookings: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/orderBookingByManual`;
    return this.http.post(url, bookings);
  }

  deleteBookingDetail(doNo: string, facility: string, bookingNo: string, userId: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/deleteBookingDetail`;
    const headers = new HttpHeaders();

    return this.http.post(url, { doNo, facility, bookingNo, userId });
  }

  rePreviewBooking(bookings: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/rePreviewBooking`;
    return this.http.post(url, bookings);
  }

  checkBookingDetailsInserable(bookingNo: string, DONos: string[]): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/insertBookingDetailValidations`;
    return this.http.post(url, { bookingNo, deliveryOrderList: DONos });
  }

  downloadShippingDocument(docType: string, bookingNo: string): Observable<any> {
    return this.documentService.downloadDocument(docType, 'Booking', bookingNo);
  }

  downloadShippingDocuments(docType: string, referenceType: string, bookingNos: string[], loginAccount: string, isAllowRemovePrinted: boolean, remark: string): Observable<any> {
    return this.documentService.downloadDocuments(docType, referenceType, bookingNos, loginAccount, isAllowRemovePrinted, remark);
  }

  getImportDocData(facility: string, bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/getImportDocData`;
    const headers = new HttpHeaders();
    const params = new HttpParams().append('facility', facility).append('bookingNo', bookingNo);

    return this.http.get(url, { headers, params });
  }

  downloadCertificateFile(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/downloadCertificateFile`;
    return this.http.post(url, param, { responseType: 'blob' });
  }

  getTmsReason(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/logistics/queryForTMS`;
    return this.http.post(url, param);
  }

  getDHLInfo(facility: string, bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/getDHLInfo/${facility}/${bookingNo}`;
    return this.http.get(url);
  }

  getDHLInvoiceSkuDescriptionOptions(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/getDHLInvoiceSkuDescriptionOptions`;
    return this.http.post(url, param);
  }

  updateDHLInvoiceHsCode(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/updateDHLInvoiceHsCode`;
    return this.http.post(url, param);
  }

  saveDHLInfo(updateObj: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/updateDHLInfo`;
    return this.http.post(url, updateObj);
  }

  dhlReturnCheck(bookingNo: string, facility: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/DHLReturnCheck/${facility}/${bookingNo}`;
    return this.http.get(url);
  }


  dhlWayBill(sendObj: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/DHLWayBill`;
    return this.http.post(url, sendObj);
  }

  isControlledItem(doNoList: any, facility: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/isControlledItem`;
    return this.http.post(url, {facility, doNoList});
  }

  checkLicenseNo(checkLicenseNoList: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/checkLicenseNo`;
    return this.http.post(url, checkLicenseNoList, {responseType: 'text'});
  }



  /**
   *
   */
  findBookingForForwarder(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/findBookingForForwarder`;
    return this.http.post(url, param);
  }

  checkIsEditCarType(bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/checkIsEditCarType/${bookingNo}`;
    return this.http.get(url);
  }

  /**
   *
   */
  bookingForwarderChange(param: any):Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/bookingForwarderChange`;
    return this.http.post(url, param);
  }

  async doDuplicatedCarTrip(param: any): Promise<any> {
    const url = `${environment.logisticsApiUrl}/booking/doDuplicatedCarTrip`;
    return await this.http.post(url, param).toPromise();
  }


  async searchB2BTmsLog(param: any): Promise<any> {
    const url = `${environment.b2bApiUrl}/tms/searchB2BTmsLog`;
    return await this.http.post(url, param).toPromise();
  }
  /**
   * upload Booking Updated Data By Excel
   * @param file upload file
   */
   parseBatchUpdateExcel(file: any): Observable<any>{
    const url = `${environment.logisticsApiUrl}/booking/parseBatchUpdateExcel`;

    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    let response = this.http.post(url, formData);
    return response;
  }

  uploadBatchUpdateExcel(uploadObj: any): Observable<any>{
    const url = `${environment.logisticsApiUrl}/booking/uploadBatchUpdateExcel`;
    return this.http.post(url, uploadObj);
  }


  updateBookingForForwarder(params: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/updateBookingForForwarder`;
    return this.http.put(url, params);
  }

  cancelBookingCheck(bookingNo: string, doNo?: string){
    const url = `${environment.logisticsApiUrl}/booking/cancelBooking/check/${bookingNo}?doNo=${doNo}`;
    return this.http.get(url);
  }


  bookingDetailStatusChangeManually(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/bookingDetailStatusChangeManually`;
    return this.http.post(url, param);
  }

  addressInfoRefresh(bookingNo: string): Observable<any>{
    const url = `${environment.logisticsApiUrl}/booking/addressInfoRefresh`;
    return this.http.post(url, bookingNo);
  }

  uploadBookingDetailSerialPda(uploadObj: any): Observable<any>{
    const url = `${environment.logisticsApiUrl}/booking/uploadBookingDetailSerialPda`;
    return this.http.post(url, uploadObj);
  }

  updateBookingRelatedBilling(requestParam: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/updateBookingRelatedBilling`;
    return this.http.post(url, requestParam);
  }

  downloadDHLInvoiceFile(request: any): Observable<InvoiceReportDto> {
    const url = `${environment.logisticsApiUrl}/forwarder/downloadDHLInvoiceFile`;
    return this.http.post<InvoiceReportDto>(url,request);
  }

  getShowCarTypeSetting(): Observable<any> {
    const url = `${environment.logisticsApiUrl}/appCsmSetting/getAppCsmSettingByCsmCode/ShowCarType`;
    return this.http.get(url);
  }

  checkNoKJ3Condition(facility: string, bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/checkNoKJ3Condition/${facility}/${bookingNo}`;
    return this.http.get(url);
  }

  checkForwarderNoKJ3Condition(facility: string, bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/checkForwarderNoKJ3Condition/${facility}/${bookingNo}`;
    return this.http.get(url);
  }

  rePrintDocByBooking(bookingNoList: string[], facility: string, userId: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/print/rePrintByBooking`;
    return this.http.get(url, {
      params: {
        bookingNoList: bookingNoList,
        facility: facility,
        userId: userId
      },
      responseType: 'json'});
  }

  updateBookingDetailDoPickUpFlag(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/updateBookingDetailDoPickUpFlag`;
    return this.http.post(url, param);
  }

  copyBillBooking(param: CopyBillBookingRequest): Observable<CopyBillBookingResponse> {
    const url = `${environment.logisticsApiUrl}/booking/copyBillBooking`;
    return this.http.post<CopyBillBookingResponse>(url, param);
  }

  sendB2BCustom(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/b2bCustom`;
    return this.http.post(url, param);
  }

  queryExportDeclarationFirstConditionByBookingNo(bookingNo: string) {
    const url = `${environment.logisticsApiUrl}/booking/queryExportDeclarationFirstConditionByBookingNo/${bookingNo}`;
    return this.http.get<ExportDeclarationFirstConditionDto | null>(url);
  }

  createExportDeclarationAndBookingByDto(param: ExportDeclarationFirstConditionDto) {
    const url = `${environment.logisticsApiUrl}/booking/createExportDeclarationAndBookingByDto`;
    return this.http.post<boolean>(url, param);
  }
  checkDoIsBooking(doNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/booking/checkDoIsBooking/${doNo}`;
    return this.http.get(url);
  }

  getForwarderInfo(facility: string, bookingNo: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/getForwarderInfo/${facility}/${bookingNo}`;
    return this.http.get(url);
  }

  updateForwarderInfo(param: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/updateForwarderInfo`;
    return this.http.post(url, param);
  }

  forwarderReturnCheck(bookingNo: string, facility: string): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/forwarderReturnCheck/${facility}/${bookingNo}`;
    return this.http.get(url);
  }

  downloadForwarderInvoiceFile(request: any): Observable<InvoiceReportDto> {
    const url = `${environment.logisticsApiUrl}/forwarder/downloadForwarderInvoiceFile`;
    return this.http.post<InvoiceReportDto>(url,request);
  }

  forwarderWayBill(sendObj: any): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/forwarderWayBill`;
    return this.http.post(url, sendObj);
  }

  forwarderFetchFedExPodByFacilityAndBookingNo(request: ForwarderFetchFedExPodByFacilityAndBookingNoRequest): Observable<any> {
    const url = `${environment.logisticsApiUrl}/forwarder/forwarderFetchFedExPodByFacilityAndBookingNo`;
    return this.http.post(url, request);
  }
}
